import { CloseOutlined } from '@ant-design/icons'
import React, {
  useState,
  useRef,
  useEffect,
  ReactNode,
  useCallback,
} from 'react'
import './style.scss'

interface Position {
  x: number
  y: number
}

interface Size {
  width: number
  height: number
}

interface ResizableModalProps {
  open?: boolean
  onOk?: () => void
  onCancel?: () => void
  onSizeChanged?: (width: number, height: number) => void
  noFooter?: boolean
  resizable?: boolean
  movable?: boolean
  title?: ReactNode
  children?: ReactNode
  className?: string
  style?: React.CSSProperties
  defaultWidth?: number
  defaultHeight?: number
}

type ResizeDirection =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'topleft'
  | 'topright'
  | 'bottomleft'
  | 'bottomright'
  | ''

const minY = 30

const ResizableModal: React.FC<ResizableModalProps> = ({
  open = false,
  onOk = () => {},
  onCancel = () => {},
  onSizeChanged,
  noFooter = false,
  resizable = true,
  movable = true,
  title = '',
  children,
  className = null,
  defaultWidth = 800,
  defaultHeight = 600,
  ...props
}) => {
  const [instanceId] = useState<string>(() =>
    Math.random().toString(36).substr(2, 9)
  )
  const [position, setPosition] = useState<Position>({ x: 300, y: 100 })
  const [size, setSize] = useState<Size>({
    width: defaultWidth,
    height: defaultHeight,
  })
  const [isDragging, setIsDragging] = useState<boolean>(false)
  const [isResizing, setIsResizing] = useState<boolean>(false)
  const [resizeDirection, setResizeDirection] = useState<ResizeDirection>('')
  const [dragOffset, setDragOffset] = useState<Position>({ x: 0, y: 0 })
  const [initialMousePos, setInitialMousePos] = useState<Position>({
    x: 0,
    y: 0,
  })
  const [initialSize, setInitialSize] = useState<Size>({ width: 0, height: 0 })
  const [initialPosition, setInitialPosition] = useState<Position>({
    x: 0,
    y: 0,
  })

  const modalRef = useRef<HTMLDivElement>(null)
  const rafRef = useRef<number | null>(null)

  // Cache các giá trị để tránh re-calculation
  const dragStateRef = useRef({
    isDragging: false,
    isResizing: false,
    resizeDirection: '' as ResizeDirection,
    dragOffset: { x: 0, y: 0 },
    initialMousePos: { x: 0, y: 0 },
    initialSize: { width: 0, height: 0 },
    initialPosition: { x: 0, y: 0 },
  })

  // Center modal when it opens
  useEffect(() => {
    if (open) {
      setPosition({
        x: Math.max(0, (window.innerWidth - size.width) / 2),
        y: Math.max(0, (window.innerHeight - size.height) / 2),
      })
    }
  }, [open])

  useEffect(() => {
    onSizeChanged?.(size.width, size.height)
  }, [])

  const handleCancelClick = (): void => {
    onCancel()
  }

  // Dragging functionality
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    const target = e.target as HTMLElement
    if (movable && target.classList.contains(`drag-handle-${instanceId}`)) {
      setIsDragging(true)
      dragStateRef.current.isDragging = true

      const rect = modalRef.current?.getBoundingClientRect()
      if (rect) {
        const offset = {
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        }
        setDragOffset(offset)
        dragStateRef.current.dragOffset = offset
      }
    }
  }

  // Resizing functionality
  const handleResizeMouseDown = (
    e: React.MouseEvent<HTMLDivElement>,
    direction: ResizeDirection
  ): void => {
    if (!resizable) {
      return
    }
    e.stopPropagation()

    const mousePos = { x: e.clientX, y: e.clientY }
    const currentSize = { ...size }
    const currentPosition = { ...position }

    setIsResizing(true)
    setResizeDirection(direction)
    setInitialMousePos(mousePos)
    setInitialSize(currentSize)
    setInitialPosition(currentPosition)

    // Update refs
    dragStateRef.current.isResizing = true
    dragStateRef.current.resizeDirection = direction
    dragStateRef.current.initialMousePos = mousePos
    dragStateRef.current.initialSize = currentSize
    dragStateRef.current.initialPosition = currentPosition
  }

  // Optimized mouse move handler với requestAnimationFrame
  const handleMouseMove = useCallback(
    (e: MouseEvent): void => {
      if (rafRef.current) {
        return
      } // Đang xử lý frame trước đó

      rafRef.current = requestAnimationFrame(() => {
        const dragState = dragStateRef.current

        if (dragState.isDragging && movable) {
          const newX = Math.max(
            0,
            Math.min(
              window.innerWidth - size.width,
              e.clientX - dragState.dragOffset.x
            )
          )
          const maxY = Math.max(minY, window.innerHeight - size.height)
          const newY = Math.max(
            minY,
            Math.min(maxY, e.clientY - dragState.dragOffset.y)
          )
          setPosition({ x: newX, y: newY })
        }

        if (dragState.isResizing && resizable) {
          const deltaX = e.clientX - dragState.initialMousePos.x
          const deltaY = e.clientY - dragState.initialMousePos.y

          let newWidth = dragState.initialSize.width
          let newHeight = dragState.initialSize.height
          let newX = dragState.initialPosition.x
          let newY = dragState.initialPosition.y

          const direction = dragState.resizeDirection
          if (direction.includes('right')) {
            newWidth = Math.max(300, dragState.initialSize.width + deltaX)
          }
          if (direction.includes('left')) {
            newWidth = Math.max(300, dragState.initialSize.width - deltaX)
            newX = Math.min(
              dragState.initialPosition.x + deltaX,
              dragState.initialPosition.x + dragState.initialSize.width - 300
            )
          }
          if (direction.includes('bottom')) {
            newHeight = Math.max(200, dragState.initialSize.height + deltaY)
          }
          if (direction.includes('top')) {
            newHeight = Math.max(200, dragState.initialSize.height - deltaY)
            newY = Math.min(
              dragState.initialPosition.y + deltaY,
              dragState.initialPosition.y + dragState.initialSize.height - 200
            )
          }

          // Boundary checks
          if (newX + newWidth > window.innerWidth) {
            newWidth = window.innerWidth - newX
          }
          if (newY < minY) {
            newHeight -= minY - newY
            newY = minY
          }
          if (newY + newHeight > window.innerHeight) {
            newHeight = window.innerHeight - newY
          }

          setSize({ width: newWidth, height: newHeight })
          setPosition({ x: newX, y: newY })
        }

        rafRef.current = null
      })
    },
    [movable, resizable, size.width, size.height]
  )

  const handleMouseUp = useCallback((): void => {
    const dragState = dragStateRef.current
    const wasResizing = dragState.isResizing
    const currentSize = size

    // Update refs
    dragState.isDragging = false
    dragState.isResizing = false
    dragState.resizeDirection = ''

    // Update state
    setIsDragging(false)
    setIsResizing(false)
    setResizeDirection('')

    // Cancel any pending RAF
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }

    // Call onResized callback when resize operation is finished
    if (wasResizing && onSizeChanged) {
      onSizeChanged(currentSize.width, currentSize.height)
    }
  }, [size, onSizeChanged])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    e.stopPropagation()
    if (e.key === 'Escape') {
      handleCancelClick()
    }
  }

  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove, { passive: true })
      document.addEventListener('mouseup', handleMouseUp, { passive: true })

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        // Cancel any pending animation frame
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current)
          rafRef.current = null
        }
      }
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp])

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
    }
  }, [])

  if (!open) {
    return null
  }

  return (
    <>
      {/* Backdrop - Always show when alwaysFocus is true */}
      {/* {alwaysFocus && (
        <div
          className="fixed inset-0 z-[100] bg-black opacity-40"
          onMouseDown={e => e.preventDefault()}
          onClick={e => e.preventDefault()}
        />
      )} */}

      {/* Modal */}
      <div
        ref={modalRef}
        className={`app-modal fixed rounded-lg shadow-2xl text-black overflow-hidden bg-white border border-gray-100 ${
          isDragging ? 'dragging' : ''
        } ${isResizing ? 'resizing' : ''}`}
        style={{
          zIndex: 9999999,
          left: `${position.x}px`,
          top: `${position.y}px`,
          width: `${size.width}px`,
          height: `${size.height}px`,
          cursor: isDragging ? 'grabbing' : 'default',
          willChange: 'transform', // Tối ưu cho GPU acceleration
        }}
        onMouseDown={handleMouseDown}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        {...props}
      >
        {/* Header with drag handle */}
        <div
          className={`${
            movable ? `drag-handle-${instanceId} ` : ''
          } text-black px-2 py-0 flex justify-between items-center`}
        >
          <div className="pointer-events-none pl-2">{title}</div>
          <div
            className="p-2 hover:bg-red-500 hover:text-white -mr-[8px]"
            onClick={handleCancelClick}
          >
            <CloseOutlined />
          </div>
        </div>
        {/* Modal Content */}
        <div
          className="overflow-auto"
          style={{
            height: '100%',
          }}
        >
          {children}
        </div>
        {/* Resize Handles - Only render if resizable */}
        {resizable && (
          <>
            {/* Top */}
            <div
              className="resize-handle absolute top-0 left-2 right-2 h-2 cursor-ns-resize"
              onMouseDown={e => handleResizeMouseDown(e, 'top')}
            />
            {/* Bottom */}
            <div
              className="resize-handle absolute bottom-0 left-2 right-2 h-2 cursor-ns-resize"
              onMouseDown={e => handleResizeMouseDown(e, 'bottom')}
            />
            {/* Left */}
            <div
              className="resize-handle absolute left-0 top-2 bottom-2 w-2 cursor-ew-resize"
              onMouseDown={e => handleResizeMouseDown(e, 'left')}
            />
            {/* Right */}
            <div
              className="resize-handle absolute right-0 top-2 bottom-2 w-2 cursor-ew-resize"
              onMouseDown={e => handleResizeMouseDown(e, 'right')}
            />
            {/* Corners */}
            <div
              className="resize-handle absolute top-0 left-0 w-2 h-2 cursor-nw-resize"
              onMouseDown={e => handleResizeMouseDown(e, 'topleft')}
            />
            <div
              className="resize-handle absolute top-0 right-0 w-2 h-2 cursor-ne-resize"
              onMouseDown={e => handleResizeMouseDown(e, 'topright')}
            />
            <div
              className="resize-handle absolute bottom-0 left-0 w-2 h-2 cursor-sw-resize"
              onMouseDown={e => handleResizeMouseDown(e, 'bottomleft')}
            />
            <div
              className="resize-handle absolute bottom-0 right-0 w-2 h-2 cursor-se-resize"
              onMouseDown={e => handleResizeMouseDown(e, 'bottomright')}
            />
          </>
        )}
      </div>
    </>
  )
}

export default ResizableModal
